import { createFileRoute } from '@tanstack/react-router';
import {
  AlertTriangle,
  Bell,
  BookPlus,
  CalendarClock,
  CalendarOff,
  CalendarRange,
  CircleCheckBig,
  Clock,
  Edit3,
  EyeOff,
  Globe,
  KeyRound,
  Languages,
  LifeBuoy,
  ListChecks,
  Mail,
  MapPin,
  MessageSquareText,
  Mic,
  Phone,
  PhoneCall,
  Plane,
  Repeat,
  ShieldCheck,
  Smartphone,
  Sparkles,
  Sunrise,
  Truck,
  UserPlus,
  Users,
  Video,
  Wrench,
} from 'lucide-react';
import { Button } from '@bookaburra/ui';
import { Section, SectionEyebrow } from '../components/Section.js';
import { usePageTitle } from '../components/usePageTitle.js';
import { signupUrl } from '../lib/urls.js';

export const Route = createFileRoute('/features')({
  component: FeaturesPage,
});

interface Feature {
  slug: string;
  Icon: typeof Sparkles;
  /** The capability, named the way a buyer would search for it. */
  title: string;
  /** One-line pain point in the operator's voice. */
  pain: string;
  /** Plain-English description of what we do about it. */
  benefit: string;
  /**
   * Tier availability — shown as a small pill on the card so buyers
   * know what's behind the paywall before they click through to pricing.
   * Default is 'all' if omitted.
   */
  tier?: 'all' | 'small_plus' | 'large_only' | 'poa';
  /** Optional "See examples" link rendered below the benefit text. */
  link?: { label: string; href: string };
  /** Optional one-line privacy footnote rendered below the benefit
   *  text. Used on AI-powered features to reassure that conversation
   *  content isn't retained — see /privacy "What we don't log". */
  privacyNote?: string;
}

interface FeatureGroup {
  eyebrow: string;
  heading: string;
  blurb: string;
  features: Feature[];
}

const GROUPS: FeatureGroup[] = [
  {
    eyebrow: 'For your clients',
    heading: 'The booking page does the heavy lifting.',
    blurb:
      'Your clients see only slots that actually exist. No back-and-forth, no double-bookings, no \u201csorry, we don\u2019t have that room today\u201d phone calls.',
    features: [
      {
        slug: 'self-serve',
        Icon: CalendarClock,
        title: '24/7 self-serve booking',
        pain:
          'Clients call at 8pm and you find out at 9am — by which point the slot\u2019s gone.',
        benefit:
          'A clean booking page that\u2019s always open. Clients pick a service, see only the slots where the right staff, room and equipment are free, and confirm — without you lifting a finger.',
      },
      {
        slug: 'mobile',
        Icon: Smartphone,
        title: 'Mobile-first',
        pain:
          'Half the bookings come in on a phone. Tiny dropdowns and unselected dates lose people.',
        benefit:
          'Designed for mobile from the first pixel. Big, obvious touch targets. Selected times unmistakable.',
      },
      {
        slug: 'languages',
        Icon: Languages,
        title: 'Use AI to translate to 11 languages',
        pain:
          'Clients who don\u2019t read English well bounce off the booking page.',
        benefit:
          'English plus Mandarin, Arabic, Vietnamese, Cantonese, Punjabi, Greek, Italian, Hindi, Spanish and Filipino — the ten most-spoken non-English languages in Australia. Switch with one tap.',
      },
      {
        slug: 'service-location',
        Icon: MapPin,
        title: 'Show where the service happens',
        pain:
          'Clients arrive at the wrong address, or wait at home for a mobile service that\u2019s actually at the clinic.',
        benefit:
          'Every service is tagged At our place, At your place, Virtual or Phone — and "At our place" badges show your actual street address so there\u2019s no confusion.',
      },
      {
        slug: 'delivery-modes',
        Icon: Video,
        title: 'In-person, on-site, video or phone',
        pain:
          'Different services need different delivery modes — one platform usually only does one.',
        benefit:
          'Mix and match per service. The booking page tells clients exactly which mode they\u2019re booking, and travel time is automatically factored in for on-site jobs.',
      },
      {
        slug: 'magic-link',
        Icon: Mail,
        title: 'Reschedule without an account',
        pain:
          'Every booking platform asks clients to sign up. They give up halfway and you get a phone call.',
        benefit:
          'Every confirmation email and reminder carries a personal magic link. Clients reschedule, cancel, or view their booking in two taps — no password, no account.',
      },
      {
        slug: 'reminders',
        Icon: Clock,
        title: 'Automatic 24-hour and 1-hour reminders',
        pain:
          'No-shows. Or you spend the morning calling tomorrow\u2019s clients.',
        benefit:
          'Email reminders go out the day before and an hour before. The link inside lets the client reschedule on the spot if something\u2019s come up.',
        link: { label: 'Preview all notifications', href: '/notifications' },
      },
      {
        slug: 'booking-assistant',
        Icon: MessageSquareText,
        title: 'Booking assistant chat',
        pain:
          'New clients aren\u2019t sure what to book. They give up and call later — or not at all.',
        benefit:
          'A guided chat that walks them through "book a service / change a booking / cancel" with one tap per choice. No phone tree. No AI hallucinations.',
        tier: 'small_plus',
        privacyNote: "Conversations aren't stored. Only the fact that one happened.",
      },
      {
        slug: 'chat-translations',
        Icon: Languages,
        title: 'Booking assistant in 11 languages',
        pain:
          'A client who reads English slowly opens the chat, sees only English, and rings the front desk instead.',
        benefit:
          'The booking-assistant chat speaks English plus Mandarin, Arabic, Vietnamese, Cantonese, Punjabi, Greek, Italian, Hindi, Spanish and Filipino — the ten most-spoken non-English languages in Australia per the ABS 2021 census. Same one-tap flow, translated end to end. On every plan, including Free — accessibility shouldn\u2019t sit behind a paywall.',
      },
      {
        slug: 'ai-voice-agent',
        Icon: Mic,
        title: 'AI voice agent',
        pain:
          'Clients want to call about a booking, but the receptionist is on the other line and the phone tree gets them nowhere.',
        benefit:
          'A web-based AI voice agent on the booking page handles bookings, reschedules, and cancels by voice — Australian English, no sign-in, no phone tree. Available on Small business and above; an Australian phone-line version is available on request for Large customers (see AI booking phone line).',
        tier: 'small_plus',
        link: { label: 'See AI booking phone line', href: '#ai-phone-line' },
        privacyNote: "Conversations aren't stored. Only the fact that one happened.",
      },
      {
        slug: 'ai-phone-line',
        Icon: PhoneCall,
        title: 'AI booking phone line',
        pain:
          'Some clients still want to call a real number. Staffing a phone line around the clock costs as much as the service itself.',
        benefit:
          'An Australian phone number answered 24/7 by the Bookaburra AI. Callers can book, reschedule, or cancel by voice — no hold music, no missed after-hours calls, no extra headcount. Available on request for Large business customers.',
        tier: 'poa',
        privacyNote: "Conversations aren't stored. Only the fact that one happened.",
      },
    ],
  },
  {
    eyebrow: 'For your team',
    heading: 'Your team actually wants to use the admin.',
    blurb:
      'Day-at-a-glance calendars, sick-day reshuffles, and per-day staff strips so the front desk knows what\u2019s coming without scrolling.',
    features: [
      {
        slug: 'staff-calendar',
        Icon: CalendarRange,
        title: 'Day-at-a-glance staff calendar',
        pain:
          'You open the admin and stare at a week. Today\u2019s detail is buried.',
        benefit:
          'Today + the next two days side by side. Each day shows every booking on the right person\u2019s schedule, and a per-day staff strip up top tells you who\u2019s in, who\u2019s sick, and who\u2019s on extra cover.',
      },
      {
        slug: 'sick-day-reshuffle',
        Icon: Sparkles,
        title: 'Sick-day reshuffle',
        pain:
          'A staff member calls in sick at 7am and three hours of bookings are now without an owner.',
        benefit:
          'Mark them unavailable for a window. Bookaburra proposes a new time for each affected booking, only substituting staff who hold all the required skills and credentials — so a physio slot is never handed to someone without the right qualifications. You approve; clients get re-confirmed by email with the new slot.',
        tier: 'large_only',
      },
      {
        slug: 'skill-aware',
        Icon: Users,
        title: 'Skill-aware staff allocation',
        pain:
          'A new starter takes a booking they\u2019re not qualified for and you don\u2019t notice until they\u2019re halfway through.',
        benefit:
          'Each service declares the credentials required (e.g. CT certified, registered nurse). Clients only see slots filled by qualified staff — period. Read the long version below: honest about what the allocator does and what it doesn’t.',
        link: { label: 'How bookings are allocated', href: '/how-bookings-are-allocated' },
      },
      {
        slug: 'resource-matching',
        Icon: Wrench,
        title: 'Resource and room matching',
        pain:
          'Two bookings, one CT room. Receptionist didn\u2019t check. Now you\u2019ve got an unhappy 9am.',
        benefit:
          'Services declare which features they need (e.g. MRI, accessible). Bookaburra picks an available room or piece of equipment for each booking — and never double-books one.',
      },
      {
        slug: 'team-invites',
        Icon: UserPlus,
        title: 'Invite teammates by email',
        pain:
          'New hire shows up and you spend twenty minutes setting up their account.',
        benefit:
          'Add someone by email. If they already have a Bookaburra account they\u2019re in. If not, they get a branded email that drops them straight into your workspace once they sign up.',
      },
      {
        slug: 'closed-days',
        Icon: CalendarOff,
        title: 'Public holidays and closed days',
        pain:
          'A client books on Anzac Day. Someone has to ring them up.',
        benefit:
          'Block out dates across your whole business. The booking page greys them out so there\u2019s nothing to refuse.',
      },
      {
        slug: 'workspaces',
        Icon: Globe,
        title: 'Multiple workspaces, one login',
        pain:
          'You run two clinics on different software. Switching is friction.',
        benefit:
          'One Bookaburra account, multiple businesses — each with its own booking page, team, and one bookable street address. Unlimited virtual and on-site services from that base. Workspace switcher in the top right of the admin — one click to jump between them.',
      },
      {
        slug: 'calendar-feed',
        Icon: Repeat,
        title: 'Calendar feed for staff',
        pain:
          'Staff want their bookings in Apple, Google or Outlook Calendar, not yet another app.',
        benefit:
          'Each staff member gets a private calendar feed link. Subscribe once; new bookings show up alongside their personal calendar automatically.',
      },
      {
        slug: 'booking-approval',
        Icon: CircleCheckBig,
        title: 'Booking approval workflow',
        pain:
          'Some bookings need a human eye — payment risk, capacity quirks, suitability. Auto-confirming everything is wrong.',
        benefit:
          'Flip a switch and new bookings hold the slot but stay pending. You get an email + magic-link to approve or reject each one with an optional message back to the customer. The customer gets a clear "pending approval" email and a confirmation (or apology) as soon as you decide.',
      },
      {
        slug: 'waiting-list',
        Icon: ListChecks,
        title: 'Waiting list when slots run out',
        pain:
          'A cancelled 11am Wednesday just disappears — clients have moved on by the time you call.',
        benefit:
          'When a day is full, clients can join a waiting list for that day. The moment a slot frees up, everyone on the list gets emailed at the same time — first to come back and book wins. You see the list on the staff calendar so you can roster more cover when demand is heavy.',
        tier: 'large_only',
      },
      {
        slug: 'notification-settings',
        Icon: Bell,
        title: 'Enable or disable any notification',
        pain:
          'Clients get a text for every booking state change. Some of those messages add noise more than value.',
        benefit:
          'Turn off any SMS or email notification type with a single toggle — without affecting the rest. Large business plan includes all 12 SMS types; you decide which ones your clients actually need.',
        tier: 'large_only',
        link: { label: 'See all notification types', href: '/notifications' },
      },
      {
        slug: 'customise-wording',
        Icon: Edit3,
        title: "Customise email + SMS wording",
        pain:
          "Generic platform copy. Your customers can tell when an email isn't actually from you.",
        benefit:
          "Edit every customer-facing email subject, email body, and SMS — using your own voice, your own greeting, your own sign-off. A live preview shows what it'll look like; a character-budget indicator warns when an SMS would split into multiple segments.",
        tier: 'large_only',
        link: { label: 'Preview all notification templates', href: '/notifications' },
      },
      {
        slug: 'roles',
        Icon: KeyRound,
        title: 'Owner, manager, staff: three levels of access',
        pain:
          'Everyone has the same login and either over-shares the keys or under-shares them — there is no middle ground.',
        benefit:
          'Three built-in roles with sensible defaults. Owners see everything, including billing and team management. Managers run the day-to-day — bookings, services, settings — but can’t touch billing or invite teammates. Regular staff land on My day, see only their own bookings, and never see the wider admin. Owners get internal RBAC chips on every page so it’s obvious who can see what. Click any booking to spawn a follow-up booking for that same client — available to owners and managers, not regular staff.',
      },
      {
        slug: 'service-config',
        Icon: EyeOff,
        title: 'Hide services from the public page, set custom hours per service',
        pain:
          'Some services aren’t for self-serve booking — they’re for staff to enter on a client’s behalf. And sometimes a single service runs on different hours from the rest of the business (Saturday emergency callouts, late-night on-call).',
        benefit:
          'Flip the bookable-by-client toggle off and a service disappears from the public booking page but stays available for managers to create bookings against. Pair it with per-service business hours and a Saturday-only emergency callout can run from 7am to 11pm while the storefront stays Mon–Fri 9–5. The allocator still respects every other guardrail — staff, skills, resources, travel — it just uses the service’s own hours instead of the business default.',
        tier: 'small_plus',
      },
      {
        slug: 'my-day',
        Icon: Sunrise,
        title: 'My day: a calmer view of tomorrow',
        pain:
          'Staff open a full week calendar to find their three appointments. The signal-to-noise is awful.',
        benefit:
          'My day is the default landing page for regular staff. It shows their bookings for the current day if there’s anything still open, otherwise it rolls forward to the next business day. Owners and managers can switch between staff to see anyone’s day. At 5pm in the business’s timezone, a daily summary email goes out to every staff member with bookings the next day — first name, locale, service, time, no contact details. Tomorrow’s diary in one glance, no login needed to read it. The CTA back into Bookaburra is a normal sign-in link, deliberately — magic links into a staff calendar would be a security smell.',
      },
      {
        slug: 'inline-help',
        Icon: LifeBuoy,
        title: 'Help is always one tap away',
        pain:
          'Staff hit a screen they’ve never used, can’t find the right answer in a help centre, and either guess or interrupt someone else.',
        benefit:
          'Every page in the app has a floating help button in the bottom-right. Tap it and you get a curated set of answers for that exact page — about 88 topics across 26 routes today, written by the people who built Bookaburra. No chat-input box, no LLM, no risk of made-up answers — just tap a question, read the answer, get on with your day. The same button on the booking-page admin shows different topics from the same button on the services list, because the questions are different.',
      },
      {
        slug: 'client-reliability',
        Icon: AlertTriangle,
        title: 'See who cancels and who no-shows',
        pain:
          "Same name keeps cancelling. Same name didn't turn up again. You only spot the pattern after it's cost you three Saturdays.",
        benefit:
          "Every cancellation records who actually clicked cancel — the client or someone on your team — so a staff sick-day doesn't make a customer look unreliable. The Clients page lists everyone with their cancellation count and no-shows over the last 30/90/180/365 days; repeat offenders sort to the top. On the calendar, a small icon next to the client name flags their last interaction — red for a no-show, amber for a cancel, sage for one we cancelled on them. We don't auto-block anyone. That call stays yours.",
      },
      {
        slug: 'admin-create-booking',
        Icon: BookPlus,
        title: 'Create bookings on behalf of clients',
        pain:
          'A client rings up wanting to book — the staff member has to walk them through the public form or jot it on paper and add it later.',
        benefit:
          'Owners and managers can create a booking directly from the admin. Last-name search is space-insensitive, so typing "McGra" finds both McGrath and Mc Grath without making you guess which way the client spells it. Pick an existing client or kick off the new-client flow inline. Click any existing booking to spawn a follow-up for that same client — service is deliberately not pre-filled so staff can offer something different from last time. Internal-only services that aren’t on the public page are bookable here.',
      },
    ],
  },
  {
    eyebrow: 'Behind the scenes',
    heading: 'Quiet, careful, and Australian.',
    blurb:
      'Bots blocked at the door, data hosted in Australia, and every change to a booking auditable. Boring, but the right kind of boring.',
    features: [
      {
        slug: 'bot-mitigation',
        Icon: ShieldCheck,
        title: 'Bot mitigation on every public form',
        pain:
          'Open booking pages get scraped and spam-booked.',
        benefit:
          'Cloudflare Turnstile sits on the booking and signup forms, transparently. Real clients see nothing; bots see no.',
      },
      {
        slug: 'audit-trail',
        Icon: CircleCheckBig,
        title: 'Soft-delete with audit trail',
        pain:
          'You retire a service or skill and old bookings show up with raw IDs because the record was hard-deleted.',
        benefit:
          'Archive instead of delete. Past bookings keep their reference. Restore anytime.',
      },
      {
        slug: 'timezones',
        Icon: Plane,
        title: 'Per-business timezones',
        pain:
          'You run a Perth clinic from Sydney. Every booking ends up in the wrong timezone.',
        benefit:
          'Each business picks its own timezone. Every booking time on the page, the admin, the confirmation email and the reminder is rendered in that zone.',
      },
      {
        slug: 'reply-to',
        Icon: Phone,
        title: 'Reply-to is your inbox',
        pain:
          'Confirmation emails reply to no-reply@platform.com and clients call instead.',
        benefit:
          'Replies to confirmation emails come straight to your business inbox, not a no-reply address.',
      },
      {
        slug: 'travel-time',
        Icon: Truck,
        title: 'Travel-time-aware on-site bookings',
        pain:
          'Mobile-trades staff get double-booked across town because the calendar doesn\u2019t know about travel.',
        benefit:
          'On-site services book with a travel buffer so back-to-back jobs across suburbs are never offered.',
      },
    ],
  },
];

function FeaturesPage() {
  usePageTitle('Features');

  return (
    <>
      {/* HERO */}
      <section className="bg-brand-cream">
        <div className="mx-auto max-w-6xl px-6 pb-14 pt-14 sm:px-12 sm:pt-20">
          <div className="inline-flex items-center gap-2 rounded-full bg-brand-cream-shadow px-3 py-1 text-xs font-semibold uppercase tracking-widest text-brand-sage ring-1 ring-brand-sage/15">
            <Sparkles className="h-4 w-4" />
            Features
          </div>
          <h1 className="mt-6 max-w-4xl text-4xl font-extrabold leading-[1.05] tracking-tightest text-brand-ink-soft sm:text-6xl lg:text-[4rem]">
            Everything Bookaburra does,{' '}
            <span className="text-brand-sage">in the boring detail.</span>
          </h1>
          <p className="mt-7 max-w-2xl text-lg text-brand-ink-soft/80 sm:text-xl">
            Each row is a real pain we've heard from service teams, and what we
            do about it. If something's missing from this list, write to us.
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <Button asChild size="lg">
              <a href={signupUrl()}>Start for free</a>
            </Button>
          </div>
        </div>
      </section>

      {GROUPS.map((group, gi) => (
        <Section key={group.heading} tone={gi % 2 === 0 ? 'shadow' : undefined}>
          <div className="max-w-3xl">
            <SectionEyebrow>{group.eyebrow}</SectionEyebrow>
            <h2 className="mt-3 text-3xl font-bold tracking-tight text-brand-ink-soft sm:text-5xl">
              {group.heading}
            </h2>
            <p
              className="mt-5 text-lg text-brand-ink-soft/75"
              dangerouslySetInnerHTML={{ __html: group.blurb }}
            />
          </div>
          <ul className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-2 lg:gap-x-10 lg:gap-y-10">
            {group.features.map((f) => (
              <li
                key={f.title}
                id={f.slug}
                className="rounded-2xl border border-brand-sage/15 bg-brand-cream p-7 shadow-[0_30px_80px_-30px_rgba(19,33,36,0.08)]"
              >
                <div className="mb-4 flex items-center justify-between gap-3">
                  <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-brand-sage/10 text-brand-sage">
                    <f.Icon className="h-5 w-5" />
                  </div>
                  <TierPill tier={f.tier ?? 'all'} />
                </div>
                <h3 className="text-xl font-bold tracking-tight text-brand-ink-soft">
                  {f.title}
                </h3>
                <p className="mt-3 text-sm font-medium text-brand-sage">
                  &ldquo;{f.pain}&rdquo;
                </p>
                <p className="mt-3 text-base leading-relaxed text-brand-ink-soft/80">
                  {f.benefit}
                </p>
                {f.link && (
                  <a
                    href={f.link.href}
                    className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-brand-sage hover:underline"
                  >
                    {f.link.label} &rarr;
                  </a>
                )}
                {f.privacyNote && (
                  <p className="mt-3 text-xs italic text-brand-ink-soft/55">
                    {f.privacyNote}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </Section>
      ))}

      {/* CTA */}
      <section className="bg-brand-ink text-brand-cream">
        <div className="mx-auto max-w-6xl px-6 py-20 sm:px-12 sm:py-24">
          <div className="grid gap-10 lg:grid-cols-[3fr_2fr] lg:items-center">
            <div>
              <h2 className="text-3xl font-extrabold tracking-tight sm:text-5xl">
                See your day on Bookaburra.
              </h2>
              <p className="mt-6 max-w-xl text-lg text-brand-cream/80">
                Free to start — up to 3 team members and 50 bookings a month.
                No card. Set it up in ten minutes.
              </p>
            </div>
            <div className="flex flex-wrap gap-3 lg:justify-end">
              <a
                href={signupUrl()}
                className="inline-flex items-center justify-center rounded-md bg-brand-cream px-6 py-3.5 text-sm font-medium text-brand-ink-soft hover:bg-brand-cream/90"
              >
                Start for free
              </a>
              <a
                href="/pricing"
                className="inline-flex items-center justify-center rounded-md border border-brand-cream/30 px-6 py-3.5 text-sm font-medium text-brand-cream hover:bg-brand-cream/10"
              >
                See pricing
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function TierPill({ tier }: { tier: 'all' | 'small_plus' | 'large_only' | 'poa' }) {
  if (tier === 'all') {
    return (
      <span className="rounded-full bg-brand-sage/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-brand-sage">
        All plans
      </span>
    );
  }
  if (tier === 'small_plus') {
    return (
      <span className="rounded-full bg-brand-gold/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-brand-ink-soft">
        Small business +
      </span>
    );
  }
  if (tier === 'poa') {
    return (
      <span className="rounded-full bg-brand-gold/20 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-brand-ink-soft/70">
        Available on request
      </span>
    );
  }
  return (
    <span className="rounded-full bg-brand-moss/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-brand-moss">
      Large business
    </span>
  );
}
